/* istanbul ignore file */
import { ProjectToolMessageCallback } from 'domain/projects/shared/projectToolsCreation';

export const ExecutionStatus = {
  SUCCESS: 'SUCCESS',
  FAIL: 'FAIL',
};

type CreateOnePasswordResponse = {
  groupsStepStatus?: AzureGroupsProvisionStepStatus;
  vaultStepStatus?: OnePasswordVaultProvisionStepStatus;
  accessStepStatus?: AccessProvisionStepStatus;
};

export type AzureGroupsProvisionStepStatus = {
  status?: string;
  groups?: AzureSingleGroupProvisionStatus[];
};

type AzureSingleGroupProvisionStatus = {
  azureId?: string;
  key?: string;
  name?: string;
  status?: string;
  errors?: any[];
};

type OnePasswordVaultProvisionStepStatus = {
  status?: string;
  vaults?: OnePasswordSingleVaultProvisionStatus[];
};

type OnePasswordSingleVaultProvisionStatus = {
  id?: string;
  key?: string;
  name?: string;
  status?: string;
  errors?: any[];
};

type AccessProvisionStepStatus = {
  status?: string;
  accesses: SingleAccessProvisionStatus[];
};

type SingleAccessProvisionStatus = {
  vaultKey?: string;
  groupKey?: string;
  status?: string;
  errors?: any[];
};

const useCreateOnePasswordResponseHandler = () => {
  const handleAzureGroupsProvisionStep = (
    groupStatus: AzureGroupsProvisionStepStatus,
    callback: ProjectToolMessageCallback,
  ) => {
    if (groupStatus.status === ExecutionStatus.SUCCESS) {
      callback({ type: 'log', level: 'success', message: '1Password Azure Groups have been created!' });
      return true;
    } else {
      let isProblemRelatedToProjectUpdate = true;
      groupStatus.groups?.forEach(group => {
        if (group?.status === ExecutionStatus.FAIL) {
          callback({
            type: 'log',
            level: 'error',
            message: `Failed to create 1Password Azure Group with key ${group?.key} due to: ${group?.errors?.join(' ')}.`,
          });
          isProblemRelatedToProjectUpdate = false;
        }
      });

      if (isProblemRelatedToProjectUpdate) {
        callback({
          type: 'log',
          level: 'error',
          message: `1Password Azure Groups have been created, but system cannot update related values for Project Entity. Please contact DaVinci Support.`,
        });
      }
    }
    return false;
  };

  const handleOnePasswordVaultsProvisionStep = (
    vaultStatus: OnePasswordVaultProvisionStepStatus,
    callback: ProjectToolMessageCallback,
  ) => {
    if (vaultStatus.status === ExecutionStatus.SUCCESS) {
      callback({ type: 'log', level: 'success', message: '1Password Vaults has been created!' });
      return true;
    } else {
      let isProblemRelatedToProjectUpdate = true;
      vaultStatus.vaults?.forEach(vault => {
        if (vault?.status === ExecutionStatus.FAIL) {
          callback({
            type: 'log',
            level: 'error',
            message: `Failed to create 1Password Vault with key ${vault?.key} due to: ${vault?.errors?.join(' ')}.`,
          });
          isProblemRelatedToProjectUpdate = false;
        }
      });

      if (isProblemRelatedToProjectUpdate) {
        callback({
          type: 'log',
          level: 'error',
          message: `1Password Vaults has been created, but system cannot update related values for Project Entity. Please contact DaVinci Support.`,
        });
      }
    }
    return false;
  };

  const handleOnePasswordAccessProvisionStep = (
    accessStatus: AccessProvisionStepStatus,
    callback: ProjectToolMessageCallback,
  ) => {
    if (accessStatus.status === ExecutionStatus.SUCCESS) {
      callback({ type: 'log', level: 'success', message: '1Password Access has been configured!' });
      return true;
    } else {
      accessStatus.accesses?.forEach(accessRecord => {
        if (accessRecord.status === ExecutionStatus.FAIL) {
          callback({
            type: 'log',
            level: 'error',
            message: `Failed to Setup access for Group with key ${accessRecord.groupKey} to Vault with key ${accessRecord.vaultKey} due to: ${accessRecord?.errors?.join(' ')}.`,
          });
        }
      });
      return false;
    }
  };
  const handleOnePasswordResponse = (
    state: CreateOnePasswordResponse,
    callbackFunction: ProjectToolMessageCallback,
  ) => {
    let isPassed = true;

    if (state.groupsStepStatus) {
      isPassed = handleAzureGroupsProvisionStep(state.groupsStepStatus, callbackFunction) && isPassed;
    }
    if (state.vaultStepStatus) {
      isPassed = handleOnePasswordVaultsProvisionStep(state.vaultStepStatus, callbackFunction) && isPassed;
    }
    if (state.accessStepStatus) {
      isPassed = handleOnePasswordAccessProvisionStep(state.accessStepStatus, callbackFunction) && isPassed;
    }

    if (!state.groupsStepStatus && !state.vaultStepStatus && !state.accessStepStatus) {
      callbackFunction({ type: 'log', level: 'success', message: '1Password Access has been configured!' });
    }

    callbackFunction({ type: 'status', status: isPassed ? 'passed' : 'failed' });
  };

  return {
    handleOnePasswordResponse,
  };
};

export default useCreateOnePasswordResponseHandler;
